import React from 'react';
import { StatusTypes, SizeVariants, CommonDefaults } from '../../../types/common';
import { PropTypes } from '../../../utils/propTypes';
import styles from './StatusBadge.module.css';

const StatusBadge = ({ 
  status, 
  size = CommonDefaults.size,
  variant,
  showIcon = true,
  className,
  style,
  'data-testid': testId
}) => {
  const getStatusConfig = (status) => {
    // Handle both enum values and raw text values
    const statusMap = {
      [StatusTypes.APPROVED]: 'Approved',
      [StatusTypes.PENDING]: 'Pending Review', 
      [StatusTypes.DRAFT]: 'Draft',
      [StatusTypes.REJECTED]: 'Rejected',
      [StatusTypes.SUCCESS]: 'Approved',
      [StatusTypes.ERROR]: 'Rejected',
      [StatusTypes.WARNING]: 'Pending Review',
      [StatusTypes.INFO]: 'Draft',
      // Raw text fallbacks
      'Approved': 'Approved',
      'Pending Review': 'Pending Review',
      'Draft': 'Draft',
      'Rejected': 'Rejected',
      'OK': 'Approved',
      'Expired': 'Rejected',
      'In Review': 'Pending Review'
    };
    
    const displayStatus = statusMap[status] || status || 'Draft';
    
    const configs = {
      'Approved': {
        className: 'approved',
        icon: (
          <svg viewBox="0 0 24 24">
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" fill="currentColor" />
          </svg>
        )
      },
      'Pending Review': {
        className: 'pending',
        icon: (
          <svg viewBox="0 0 24 24">
            <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M7,13H11V7H13V13H17L12,18L7,13Z" fill="currentColor" />
          </svg>
        )
      },
      'Draft': {
        className: 'draft',
        icon: (
          <svg viewBox="0 0 24 24">
            <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M1,18V21H4L15,10L12,7L1,18Z" fill="currentColor" />
          </svg>
        )
      },
      'Rejected': {
        className: 'rejected',
        icon: (
          <svg viewBox="0 0 24 24">
            <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" fill="currentColor" />
          </svg>
        )
      }
    };
    
    return {
      ...configs[displayStatus] || configs['Draft'],
      displayStatus
    };
  };

  const config = getStatusConfig(status);
  
  const badgeClasses = [
    styles.statusBadge,
    styles[config.className],
    styles[size],
    variant && styles[variant],
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={badgeClasses}
      style={style}
      data-testid={testId}
      role="status"
      aria-label={`Status: ${config.displayStatus}`}
    >
      {showIcon && (
        <div className={styles.statusIcon}>
          {config.icon}
        </div>
      )}
      <span className={styles.statusText}>{config.displayStatus}</span>
    </div>
  );
};

StatusBadge.displayName = 'StatusBadge';

// PropTypes for runtime validation
StatusBadge.propTypes = {
  status: PropTypes.status.isRequired,
  size: PropTypes.size,
  variant: PropTypes.theme,
  showIcon: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  'data-testid': PropTypes.string
};

export default StatusBadge;